import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, AlertTriangle, Search, RefreshCw, ExternalLink, Filter,
  ChevronLeft, ChevronRight, AlertCircle, CheckCircle, Info, Zap, Globe,
  Calendar, Languages, Clock, TrendingUp, X, Plus, Edit2, Trash2,
  Link as LinkIcon, Save, XCircle,
} from 'lucide-react';
import { apiService } from '../services/api';
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Vulnerability {
  id: number;
  cve_id: string;
  title: string;
  description: string;
  description_tr: string | null;
  source: 'NVD' | 'CISA_KEV';
  severity: string;
  cvss_score: number | null;
  cvss_version: string | null;
  published_date: string | null;
  modified_date: string | null;
  is_actively_exploited: number;
  vendor_project: string | null;
  product: string | null;
  required_action: string | null;
  solution_count: number;
  created_at: string;
}

interface Solution {
  id: number;
  vulnerability_id: number;
  content: string;
  link_url: string | null;
  link_label: string | null;
  link_type: 'fix' | 'patch' | 'workaround' | 'reference' | 'other';
  created_by: number;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

interface Stats {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low_count: number;
  actively_exploited: number;
  new_today: number;
  from_cisa: number;
  from_nvd: number;
}

interface FetchLog {
  id: number;
  source: string;
  count_fetched: number;
  count_new: number;
  status: 'success' | 'error';
  error_message: string | null;
  created_at: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// ─── Sabitler ─────────────────────────────────────────────────────────────────
const SEVERITY_CONFIG: Record<string, { severityKey: string; color: string; bg: string; border: string }> = {
  CRITICAL: { severityKey: 'secvuln.severityCritical', color: 'text-red-700 dark:text-red-400',       bg: 'bg-red-100 dark:bg-red-900/30',       border: 'border-red-300 dark:border-red-700' },
  HIGH:     { severityKey: 'secvuln.severityHigh',     color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-900/30', border: 'border-orange-300 dark:border-orange-700' },
  MEDIUM:   { severityKey: 'secvuln.severityMedium',   color: 'text-yellow-700 dark:text-yellow-400', bg: 'bg-yellow-100 dark:bg-yellow-900/30', border: 'border-yellow-300 dark:border-yellow-700' },
  LOW:      { severityKey: 'secvuln.severityLow',      color: 'text-green-700 dark:text-green-400',   bg: 'bg-green-100 dark:bg-green-900/30',   border: 'border-green-300 dark:border-green-700' },
  NONE:     { severityKey: 'secvuln.severityNone',     color: 'text-gray-600 dark:text-gray-400',     bg: 'bg-gray-100 dark:bg-gray-700 dark:bg-gray-800',        border: 'border-gray-300 dark:border-gray-600' },
  UNKNOWN:  { severityKey: 'secvuln.severityUnknown',  color: 'text-gray-600 dark:text-gray-400',     bg: 'bg-gray-100 dark:bg-gray-700 dark:bg-gray-800',        border: 'border-gray-300 dark:border-gray-600' },
};

const LINK_TYPE_KEYS: Record<string, string> = {
  fix: 'secvuln.linkTypeFix', patch: 'secvuln.linkTypePatch', workaround: 'secvuln.linkTypeWorkaround', reference: 'secvuln.linkTypeReference', other: 'secvuln.linkTypeOther',
};

const PERIOD_OPTION_VALUES = ['1w', '2w', '1m', 'all'] as const;
const PERIOD_OPTION_KEYS: Record<string, string> = {
  '1w': 'secvuln.period1w',
  '2w': 'secvuln.period2w',
  '1m': 'secvuln.period1m',
  'all': 'secvuln.periodAll',
};

// ─── Yardımcı fonksiyonlar ────────────────────────────────────────────────────
function getSeverityConfig(severity: string) {
  return SEVERITY_CONFIG[severity?.toUpperCase()] ?? SEVERITY_CONFIG.UNKNOWN;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function timeAgo(dateStr: string | null, t: (key: string, params?: Record<string, string | number>) => string): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return t('secvuln.timeToday');
  if (days === 1) return t('secvuln.timeYesterday');
  if (days < 7) return t('secvuln.timeDaysAgo', { days });
  if (days < 30) return t('secvuln.timeWeeksAgo', { weeks: Math.floor(days / 7) });
  if (days < 365) return t('secvuln.timeMonthsAgo', { months: Math.floor(days / 30) });
  return t('secvuln.timeYearsAgo', { years: Math.floor(days / 365) });
}

// ─── Alt bileşenler ───────────────────────────────────────────────────────────
const StatCard: React.FC<{
  icon: React.ReactNode; label: string; value: number | string;
  color: string; bg: string; onClick?: () => void;
}> = ({ icon, label, value, color, bg, onClick }) => (
  <div
    className={`bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 flex items-center gap-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    onClick={onClick}
  >
    <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
      <div className={color}>{icon}</div>
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  </div>
);

const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => {
  const { t } = useLanguage();
  const cfg = getSeverityConfig(severity);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
      {t(cfg.severityKey)}
      {severity?.toUpperCase() === 'CRITICAL' && <AlertCircle className="w-3 h-3 ml-1" />}
    </span>
  );
};

const SourceBadge: React.FC<{ source: string }> = ({ source }) => (
  source === 'CISA_KEV' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700">
      <Zap className="w-3 h-3" /> CISA KEV
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-primary/20 dark:bg-primary/30 text-primary-dark dark:text-primary border border-primary/50 dark:border-primary-dark">
      <Globe className="w-3 h-3" /> NVD
    </span>
  )
);

// ─── Çözümler bölümü ──────────────────────────────────────────────────────────
interface SolutionsSectionProps {
  vulnId: number;
  canEdit: boolean;
  currentUserId: number;
  isSuperadmin: boolean;
}

const SolutionsSection: React.FC<SolutionsSectionProps> = ({ vulnId, canEdit, currentUserId, isSuperadmin }) => {
  const { showNotification } = useNotifications();
  const { t } = useLanguage();
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [content, setContent] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [linkType, setLinkType] = useState<string>('fix');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.get(`/security-vulnerabilities/${vulnId}/solutions`);
      setSolutions(data.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [vulnId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setContent(''); setLinkUrl(''); setLinkLabel(''); setLinkType('fix');
    setEditingId(null); setShowForm(false);
  };

  const startEdit = (s: Solution) => {
    setContent(s.content);
    setLinkUrl(s.link_url || '');
    setLinkLabel(s.link_label || '');
    setLinkType(s.link_type);
    setEditingId(s.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!content.trim()) { showNotification(t('secvuln.solutionRequired'), 'error'); return; }
    setSaving(true);
    try {
      const payload = { content, link_url: linkUrl || undefined, link_label: linkLabel || undefined, link_type: linkType };
      if (editingId) {
        await apiService.put(`/security-vulnerabilities/solutions/${editingId}`, payload);
        showNotification(t('secvuln.solutionUpdated'), 'success');
      } else {
        await apiService.post(`/security-vulnerabilities/${vulnId}/solutions`, payload);
        showNotification(t('secvuln.solutionAdded'), 'success');
      }
      resetForm();
      await load();
    } catch {
      showNotification(t('secvuln.solutionSaveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s: Solution) => {
    if (!confirm(t('secvuln.solutionDeleteConfirm', { preview: s.content.substring(0, 60) }))) return;
    try {
      await apiService.delete(`/security-vulnerabilities/solutions/${s.id}`);
      showNotification(t('secvuln.solutionDeleted'), 'success');
      await load();
    } catch {
      showNotification(t('secvuln.solutionDeleteError'), 'error');
    }
  };

  return (
    <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
      {/* Başlık */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
          {t('secvuln.solutionsTitle')}
          {solutions.length > 0 && (
            <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs px-1.5 rounded-full">{solutions.length}</span>
          )}
        </h4>
        {canEdit && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 text-xs text-primary dark:text-primary hover:text-primary-dark dark:hover:text-accent font-medium"
          >
            <Plus className="w-3.5 h-3.5" /> {t('secvuln.addSolution')}
          </button>
        )}
      </div>

      {/* Yükleniyor */}
      {loading && (
        <div className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400 py-2">{t('secvuln.solutionsLoading')}</div>
      )}

      {/* Çözüm listesi */}
      {!loading && solutions.length === 0 && !showForm && (
        <p className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400 italic py-1">
          {t('secvuln.noSolutions')}
          {canEdit && t('secvuln.noSolutionsHint')}
        </p>
      )}

      {!loading && solutions.map((s) => (
        <div key={s.id} className="mb-2 p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed flex-1">{s.content}</p>
            {canEdit && (s.created_by === currentUserId || isSuperadmin) && editingId !== s.id && (
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => startEdit(s)} className="p-1 text-gray-400 hover:text-primary dark:hover:text-primary rounded transition-colors">
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDelete(s)} className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
          {s.link_url && (
            <a
              href={s.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-xs text-primary dark:text-primary hover:underline"
            >
              <LinkIcon className="w-3 h-3" />
              {s.link_label || s.link_url}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
              {LINK_TYPE_KEYS[s.link_type] ? t(LINK_TYPE_KEYS[s.link_type]) : s.link_type}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">
              {s.created_by_name} · {timeAgo(s.created_at)}
            </span>
          </div>
        </div>
      ))}

      {/* Çözüm formu */}
      {showForm && canEdit && (
        <div className="p-3 bg-primary/10 dark:bg-primary/10 border border-primary/30 dark:border-primary-dark rounded-lg space-y-2 mt-2">
          <p className="text-xs font-semibold text-primary-dark dark:text-primary">
            {editingId ? t('secvuln.solutionFormEditTitle') : t('secvuln.solutionFormNewTitle')}
          </p>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            placeholder={t('secvuln.solutionPlaceholder')}
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-2">
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder={t('secvuln.linkUrlPlaceholder')}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={linkType}
                onChange={(e) => setLinkType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {Object.keys(LINK_TYPE_KEYS).map((v) => (
                  <option key={v} value={v}>{t(LINK_TYPE_KEYS[v])}</option>
                ))}
              </select>
            </div>
          </div>
          {linkUrl && (
            <input
              type="text"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              placeholder={t('secvuln.linkLabelPlaceholder')}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          )}
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 bg-primary hover:bg-primary-dark disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? t('secvuln.saving') : t('secvuln.save')}
            </button>
            <button
              onClick={resetForm}
              className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 rounded-lg text-sm transition-colors"
            >
              <XCircle className="w-3.5 h-3.5" /> {t('secvuln.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────
const SecurityVulnerabilitiesPage: React.FC = () => {
  const { showNotification } = useNotifications();
  const { user } = useAuth();
  const { t } = useLanguage();
  const isSuperadmin = user?.role === 'superadmin';
  const canEdit = user?.role === 'superadmin' || user?.role === 'admin';

  // Data
  const [vulnerabilities, setVulnerabilities] = useState<Vulnerability[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [fetchLogs, setFetchLogs] = useState<FetchLog[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ total: 0, page: 1, limit: 20, pages: 0 });

  // UI state
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [showTurkish, setShowTurkish] = useState(false);
  const [translatingId, setTranslatingId] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Filtreler
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [activeOnly, setActiveOnly] = useState(false);
  const [period, setPeriod] = useState('1w');
  const [page, setPage] = useState(1);

  // ── Veri yükleme ──────────────────────────────────────────────────────────
  const loadVulnerabilities = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(p),
        limit: '20',
        period,
        ...(source !== 'all' && { source }),
        ...(severity !== 'all' && { severity }),
        ...(search && { search }),
        ...(activeOnly && { activeOnly: 'true' }),
      });
      const data = await apiService.get(`/security-vulnerabilities?${params}`);
      setVulnerabilities(data.data);
      setPagination(data.pagination);
    } catch {
      showNotification(t('secvuln.errorLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [source, severity, search, activeOnly, period]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await apiService.get('/security-vulnerabilities/stats');
      setStats(data.stats);
      setFetchLogs(data.lastFetches || []);
    } catch { /* silent */ } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    setPage(1);
    loadVulnerabilities(1);
  }, [source, severity, search, activeOnly, period]);

  useEffect(() => { loadVulnerabilities(page); }, [page]);

  // ── Manuel güncelleme (sadece superadmin) ─────────────────────────────────
  const handleManualFetch = async () => {
    if (fetching) return;
    setFetching(true);
    try {
      await apiService.post('/security-vulnerabilities/fetch', { daysBack: 7 });
      showNotification(t('secvuln.fetchStarted'), 'success');
      setTimeout(() => { loadStats(); loadVulnerabilities(1); }, 5000);
    } catch {
      showNotification(t('secvuln.fetchError'), 'error');
    } finally {
      setTimeout(() => setFetching(false), 3000);
    }
  };

  // ── Türkçe çeviri ─────────────────────────────────────────────────────────
  const handleTranslate = async (vuln: Vulnerability) => {
    if (vuln.description_tr || translatingId === vuln.id) return;
    setTranslatingId(vuln.id);
    try {
      const data = await apiService.post(`/security-vulnerabilities/${vuln.id}/translate`, {});
      setVulnerabilities((prev) =>
        prev.map((v) => (v.id === vuln.id ? { ...v, description_tr: data.description_tr } : v))
      );
    } catch {
      showNotification(t('secvuln.translateError'), 'error');
    } finally {
      setTranslatingId(null);
    }
  };

  const handleLanguageToggle = () => {
    const next = !showTurkish;
    setShowTurkish(next);
    if (next) vulnerabilities.forEach((v) => { if (!v.description_tr) handleTranslate(v); });
  };

  // ── Filtre temizle ────────────────────────────────────────────────────────
  const resetFilters = () => {
    setSearch(''); setSource('all'); setSeverity('all'); setActiveOnly(false);
    setPeriod('1w'); setPage(1);
  };

  const hasActiveFilters = search || source !== 'all' || severity !== 'all' || activeOnly || period !== '1w';
  const lastSuccess = fetchLogs.find((l) => l.status === 'success');

  return (
    <div className="p-4 sm:p-6 max-w-full">

      {/* ── Sayfa başlığı ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('secvuln.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              CVE.org · NIST NVD · CISA KEV
              {lastSuccess && <span className="ml-2 text-xs text-gray-400">· {t('secvuln.lastUpdated', { time: timeAgo(lastSuccess.created_at, t) })}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleLanguageToggle}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              showTurkish ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700'
            }`}
          >
            <Languages className="w-4 h-4" />{showTurkish ? 'TR' : 'EN'}
          </button>
          {isSuperadmin && (
            <button
              onClick={handleManualFetch}
              disabled={fetching}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${fetching ? 'animate-spin' : ''}`} />
              {fetching ? t('secvuln.updating') : t('secvuln.update')}
            </button>
          )}
        </div>
      </div>

      {/* ── İstatistik kartları ── */}
      {statsLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[...Array(6)].map((_, i) => <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 h-20 animate-pulse" />)}
        </div>
      ) : stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <StatCard icon={<Shield className="w-5 h-5" />} label={t('secvuln.statTotal')} value={stats.total} color="text-primary dark:text-primary" bg="bg-primary/20 dark:bg-primary/30" />
          <StatCard icon={<AlertCircle className="w-5 h-5" />} label={t('secvuln.statCritical')} value={stats.critical} color="text-red-600 dark:text-red-400" bg="bg-red-100 dark:bg-red-900/30" onClick={() => { setSeverity('CRITICAL'); setPage(1); }} />
          <StatCard icon={<AlertTriangle className="w-5 h-5" />} label={t('secvuln.statHigh')} value={stats.high} color="text-orange-600 dark:text-orange-400" bg="bg-orange-100 dark:bg-orange-900/30" onClick={() => { setSeverity('HIGH'); setPage(1); }} />
          <StatCard icon={<Zap className="w-5 h-5" />} label={t('secvuln.statActiveExploit')} value={stats.actively_exploited} color="text-red-600 dark:text-red-400" bg="bg-red-100 dark:bg-red-900/30" onClick={() => { setActiveOnly(true); setPage(1); }} />
          <StatCard icon={<TrendingUp className="w-5 h-5" />} label={t('secvuln.statNewToday')} value={stats.new_today} color="text-green-600 dark:text-green-400" bg="bg-green-100 dark:bg-green-900/30" />
          <StatCard icon={<Globe className="w-5 h-5" />} label={t('secvuln.statCisaKev')} value={stats.from_cisa} color="text-purple-600 dark:text-purple-400" bg="bg-purple-100 dark:bg-purple-900/30" onClick={() => { setSource('CISA_KEV'); setPage(1); }} />
        </div>
      )}

      {/* ── Bilgi bandı ── */}
      <div className="bg-primary/10 dark:bg-primary/20 border border-primary/30 dark:border-primary-dark rounded-xl p-4 mb-5 flex items-start gap-3">
        <Info className="w-5 h-5 text-primary dark:text-primary flex-shrink-0 mt-0.5" />
        <p className="text-sm text-primary-dark dark:text-accent"
          dangerouslySetInnerHTML={{ __html: t('secvuln.infoBand') + (canEdit ? t('secvuln.infoBandAdmin') : '') }}
        />
      </div>

      {/* ── Dönem filtresi (pill) ── */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mr-1">{t('secvuln.periodLabel')}</span>
        {PERIOD_OPTION_VALUES.map((val) => (
          <button
            key={val}
            onClick={() => { setPeriod(val); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              period === val
                ? 'bg-primary text-white border-primary'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700'
            }`}
          >
            {t(PERIOD_OPTION_KEYS[val])}
          </button>
        ))}
      </div>

      {/* ── Arama & Filtreler ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-5">
        <div className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('secvuln.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-400">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                showFilters || (source !== 'all' || severity !== 'all' || activeOnly)
                  ? 'bg-primary/10 dark:bg-primary/30 border-primary/50 dark:border-primary text-primary-dark dark:text-primary'
                  : 'bg-white dark:bg-gray-800 dark:bg-gray-700 border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600'
              }`}
            >
              <Filter className="w-4 h-4" /> {t('secvuln.filterBtn')}
              {(source !== 'all' || severity !== 'all' || activeOnly) && <span className="ml-1 w-2 h-2 bg-primary rounded-full" />}
            </button>
            {hasActiveFilters && (
              <button onClick={resetFilters} className="px-3 py-2 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                {t('secvuln.clearFilters')}
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 border-t border-gray-100 dark:border-gray-700 pt-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('secvuln.filterSource')}</label>
              <select value={source} onChange={(e) => setSource(e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="all">{t('secvuln.filterSourceAll')}</option>
                <option value="CISA_KEV">CISA KEV</option>
                <option value="NVD">NIST NVD</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('secvuln.filterSeverity')}</label>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="all">{t('secvuln.filterSeverityAll')}</option>
                <option value="CRITICAL">{t('secvuln.filterSeverityCritical')}</option>
                <option value="HIGH">{t('secvuln.filterSeverityHigh')}</option>
                <option value="MEDIUM">{t('secvuln.filterSeverityMedium')}</option>
                <option value="LOW">{t('secvuln.filterSeverityLow')}</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setActiveOnly(!activeOnly)}
                className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  activeOnly ? 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-600 text-red-700 dark:text-red-400' : 'bg-white dark:bg-gray-800 dark:bg-gray-700 border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Zap className="w-4 h-4" /> {t('secvuln.filterActiveOnly')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Sonuç sayısı ── */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {loading ? t('common.loading') : t('secvuln.resultCount', { count: pagination.total })}
        </p>
        {pagination.pages > 1 && <p className="text-sm text-gray-500 dark:text-gray-400">{t('secvuln.pageOf', { page: pagination.page, pages: pagination.pages })}</p>}
      </div>

      {/* ── Liste ── */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-600 dark:bg-gray-700 rounded w-1/4 mb-2" />
              <div className="h-3 bg-gray-100 dark:bg-gray-700/50 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : vulnerabilities.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Shield className="w-12 h-12 text-gray-300 dark:text-gray-600 dark:text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">{t('secvuln.emptyTitle')}</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 dark:text-gray-400 mt-1">
            {hasActiveFilters ? t('secvuln.emptyHintFiltered') : t('secvuln.emptyHintNoData')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {vulnerabilities.map((vuln) => {
            const sev = getSeverityConfig(vuln.severity);
            const isExpanded = expandedId === vuln.id;
            const displayDesc = showTurkish ? (vuln.description_tr || vuln.description) : vuln.description;

            return (
              <div
                key={vuln.id}
                className={`bg-white dark:bg-gray-800 rounded-xl border shadow-sm transition-shadow hover:shadow-md ${
                  vuln.is_actively_exploited ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                {/* Kart başlığı */}
                <div className="p-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : vuln.id)}>
                  <div className="flex flex-wrap items-start gap-2 mb-2">
                    <a
                      href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 font-mono font-bold text-primary dark:text-primary hover:underline text-sm"
                    >
                      {vuln.cve_id} <ExternalLink className="w-3 h-3" />
                    </a>
                    <SeverityBadge severity={vuln.severity} />
                    <SourceBadge source={vuln.source} />
                    {!!vuln.is_actively_exploited && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-red-600 text-white">
                        <Zap className="w-3 h-3" /> {t('secvuln.activeExploitBadge')}
                      </span>
                    )}
                    {vuln.cvss_score !== null && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold ${sev.bg} ${sev.color} border ${sev.border}`}>
                        CVSS {parseFloat(String(vuln.cvss_score)).toFixed(1)} {vuln.cvss_version && `(v${vuln.cvss_version})`}
                      </span>
                    )}
                    {vuln.solution_count > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700">
                        <CheckCircle className="w-3 h-3" /> {t('secvuln.solutionCount', { count: vuln.solution_count })}
                      </span>
                    )}
                  </div>

                  {vuln.title && vuln.title !== vuln.cve_id && (
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1 leading-snug">{vuln.title}</p>
                  )}

                  <p className={`text-sm text-gray-600 dark:text-gray-400 leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
                    {displayDesc || '—'}
                    {showTurkish && !vuln.description_tr && <span className="text-gray-400 italic ml-1">{t('secvuln.noTranslation')}</span>}
                  </p>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">
                    {vuln.published_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {formatDate(vuln.published_date)} · {timeAgo(vuln.published_date, t)}
                      </span>
                    )}
                    {vuln.vendor_project && (
                      <span className="flex items-center gap-1">
                        <Info className="w-3 h-3" /> {vuln.vendor_project}{vuln.product ? ` / ${vuln.product}` : ''}
                      </span>
                    )}
                    <span className="flex items-center gap-1 ml-auto">
                      <Clock className="w-3 h-3" /> {timeAgo(vuln.created_at, t)}
                    </span>
                  </div>
                </div>

                {/* Genişletilmiş detay */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-3 space-y-3">
                    {/* Açıklama */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          {showTurkish ? t('secvuln.descriptionTr') : t('secvuln.descriptionEn')}
                        </p>
                        {showTurkish && !vuln.description_tr && (
                          <button
                            onClick={() => handleTranslate(vuln)}
                            disabled={translatingId === vuln.id}
                            className="text-xs text-primary dark:text-primary hover:underline disabled:opacity-50 flex items-center gap-1"
                          >
                            <Languages className="w-3 h-3" />
                            {translatingId === vuln.id ? t('secvuln.translating') : t('secvuln.translate')}
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{displayDesc || '—'}</p>
                    </div>

                    {/* CISA gerekli eylem */}
                    {vuln.required_action && (
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                        <p className="text-xs font-semibold text-orange-700 dark:text-orange-400 uppercase mb-1">{t('secvuln.requiredAction')}</p>
                        <p className="text-sm text-orange-800 dark:text-orange-300">{vuln.required_action}</p>
                      </div>
                    )}

                    {/* Kaynak linkleri */}
                    <div className="flex flex-wrap gap-2">
                      <a href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-primary/30 dark:border-primary-dark text-primary-dark dark:text-primary text-xs hover:bg-primary/10 dark:hover:bg-primary/30 transition-colors">
                        <Globe className="w-3 h-3" /> NIST NVD
                      </a>
                      <a href={`https://www.cve.org/CVERecord?id=${vuln.cve_id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 dark:text-gray-400 text-xs hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors">
                        <ExternalLink className="w-3 h-3" /> CVE.org
                      </a>
                      {vuln.source === 'CISA_KEV' && (
                        <a href="https://www.cisa.gov/known-exploited-vulnerabilities-catalog" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 text-xs hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                          <Zap className="w-3 h-3" /> {t('secvuln.cisaKevCatalog')}
                        </a>
                      )}
                    </div>

                    {/* Çözümler */}
                    <SolutionsSection
                      vulnId={vuln.id}
                      canEdit={canEdit}
                      currentUserId={user?.id ?? 0}
                      isSuperadmin={isSuperadmin}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Sayfalama ── */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors">
            <ChevronLeft className="w-4 h-4" /> {t('secvuln.prevPage')}
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
              let p: number;
              if (pagination.pages <= 7) p = i + 1;
              else if (page <= 4) p = i + 1;
              else if (page >= pagination.pages - 3) p = pagination.pages - 6 + i;
              else p = page - 3 + i;
              return (
                <button key={p} onClick={() => setPage(p)} className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${p === page ? 'bg-primary text-white' : 'border border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700'}`}>
                  {p}
                </button>
              );
            })}
          </div>
          <button onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages} className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors">
            {t('secvuln.nextPage')} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Güncelleme geçmişi ── */}
      {fetchLogs.length > 0 && (
        <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Clock className="w-4 h-4" /> {t('secvuln.updateHistory')}
            </h2>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {fetchLogs.map((log) => (
              <div key={log.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {log.status === 'success' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertCircle className="w-4 h-4 text-red-500" />}
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{log.source}</span>
                  {log.status === 'error' && <span className="text-xs text-red-500">{log.error_message}</span>}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span>{t('secvuln.newOf', { new: log.count_new, fetched: log.count_fetched })}</span>
                  <span>{timeAgo(log.created_at, t)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityVulnerabilitiesPage;
